import { useState } from 'react';

import { Badge, Button, StatusDot } from '@/components/atoms';
import {
  useCreateSite,
  useDeleteSite,
  useSites,
  useTestSite,
  useUpdateSite,
} from '@/hooks/useConfig';
import { ApiError } from '@/services/api';
import type { AuthType, WpSite, WpSiteCreate } from '@/types/config';

import styles from './ConfigSites.module.css';

interface SiteFormState {
  slug: string;
  name: string;
  base_url: string;
  rest_path: string;
  auth_type: AuthType;
  auth_user: string;
  auth_secret: string;
  default_status: string;
  default_author: string; // string in form, parsed on submit
  notes: string;
  field_map: Record<string, string>;
}

// Variáveis do lado Wiredesk que podem ser mapeadas para campos custom do WP.
// O cliente preenche a chave de meta do tema dele (precisa estar registrada com
// show_in_rest no WP). Vazio = não envia.
const PUBLISHER_VARS: { key: string; label: string; placeholder: string }[] = [
  { key: 'chapeu', label: 'Chapéu', placeholder: 'ex. ncnews: _mtb_materia_chapeu' },
  { key: 'linha_fina', label: 'Linha fina', placeholder: 'ex. ncnews: _mtb_materia_linhafina' },
  { key: 'creditos_imagem', label: 'Créditos da imagem', placeholder: 'ex. ncnews: _mtb_photo_credit' },
  { key: 'focus_keyword', label: 'Palavra-chave SEO', placeholder: 'ex.: rank_math_focus_keyword' },
  { key: 'meta_description', label: 'Meta description SEO', placeholder: 'ex.: rank_math_description' },
];

const EMPTY_FORM: SiteFormState = {
  slug: '',
  name: '',
  base_url: '',
  rest_path: '/wp-json/wp/v2',
  auth_type: 'app_password',
  auth_user: '',
  auth_secret: '',
  default_status: 'publish',
  default_author: '',
  notes: '',
  field_map: {},
};

function describeError(e: unknown): string {
  if (e instanceof ApiError) {
    const detail = (e.body as { detail?: string } | null)?.detail;
    if (detail) return detail;
    return `${e.status} ${e.message}`;
  }
  return String(e);
}

export function ConfigSites() {
  const { data: sites = [], isLoading } = useSites();
  const createMut = useCreateSite();
  const updateMut = useUpdateSite();
  const deleteMut = useDeleteSite();
  const testMut = useTestSite();

  const [editing, setEditing] = useState<WpSite | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<SiteFormState>(EMPTY_FORM);
  const [msg, setMsg] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<
    Record<string, { ok: boolean; text: string }>
  >({});

  function openCreate() {
    setForm(EMPTY_FORM);
    setEditing(null);
    setCreating(true);
    setMsg(null);
  }

  function openEdit(site: WpSite) {
    setForm({
      slug: site.slug,
      name: site.name,
      base_url: site.base_url,
      rest_path: site.rest_path,
      auth_type: site.auth_type,
      auth_user: site.auth_user ?? '',
      auth_secret: '',
      default_status: site.default_status,
      default_author: site.default_author?.toString() ?? '',
      notes: site.notes ?? '',
      field_map: { ...(site.field_map ?? {}) },
    });
    setEditing(site);
    setCreating(false);
    setMsg(null);
  }

  function closeModal() {
    setCreating(false);
    setEditing(null);
    setMsg(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setMsg(null);
    const payload: WpSiteCreate = {
      slug: form.slug.trim(),
      name: form.name.trim(),
      base_url: form.base_url.trim(),
      rest_path: form.rest_path.trim() || '/wp-json/wp/v2',
      auth_type: form.auth_type,
      auth_user: form.auth_user.trim() || null,
      default_status: form.default_status,
      default_author: form.default_author
        ? Number(form.default_author)
        : null,
      notes: form.notes.trim() || null,
      field_map: Object.fromEntries(
        Object.entries(form.field_map)
          .map(([k, v]) => [k, (v ?? '').trim()] as const)
          .filter(([, v]) => v),
      ),
    };
    if (form.auth_secret) {
      payload.auth_secret = form.auth_secret;
    }
    try {
      if (editing) {
        await updateMut.mutateAsync({ id: editing.id, payload });
        setMsg('Site atualizado.');
      } else {
        await createMut.mutateAsync(payload);
        setMsg('Site criado.');
      }
      closeModal();
    } catch (err) {
      setMsg(`Erro: ${describeError(err)}`);
    }
  }

  async function handleTest(site: WpSite) {
    setTestResults((prev) => ({
      ...prev,
      [site.id]: { ok: false, text: 'A testar…' },
    }));
    try {
      const res = await testMut.mutateAsync(site.id);
      setTestResults((prev) => ({
        ...prev,
        [site.id]: {
          ok: res.ok,
          text: res.elapsed_ms
            ? `${res.message} · ${res.elapsed_ms}ms`
            : res.message,
        },
      }));
    } catch (err) {
      setTestResults((prev) => ({
        ...prev,
        [site.id]: { ok: false, text: `Erro: ${describeError(err)}` },
      }));
    }
  }

  async function handleDelete(site: WpSite) {
    if (
      !window.confirm(
        `Pausar (soft-delete) o site "${site.name}"? Workflows e artigos ligados a ele continuam, ` +
          `mas o site deixa de aparecer nas dropdowns activas.`,
      )
    ) {
      return;
    }
    try {
      await deleteMut.mutateAsync(site.id);
      setMsg(`Site "${site.name}" pausado.`);
    } catch (err) {
      setMsg(`Erro: ${describeError(err)}`);
    }
  }

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <h1 className={styles.title}>Sites WordPress</h1>
        <Button onClick={openCreate}>+ Adicionar site</Button>
      </header>

      {msg && <p className={styles.flash}>{msg}</p>}

      {isLoading && <p className={styles.muted}>A carregar sites…</p>}
      {!isLoading && sites.length === 0 && (
        <p className={styles.muted}>
          Nenhum site WordPress cadastrado. Clica em <strong>+ Adicionar site</strong>{' '}
          para configurar o primeiro.
        </p>
      )}

      <ul className={styles.list}>
        {sites.map((site) => {
          const test = testResults[site.id];
          return (
            <li key={site.id} className={styles.card}>
              <div className={styles.cardHead}>
                <span className={styles.cardTitle}>{site.name}</span>
                <Badge tone={site.active ? 'ok' : 'neutral'} size="sm">
                  {site.active ? 'activo' : 'pausado'}
                </Badge>
                <span className={styles.slug}>/{site.slug}</span>
              </div>

              <div className={styles.row}>
                <span className={styles.label}>URL</span>
                <a
                  href={site.base_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.link}
                >
                  {site.base_url}
                </a>
              </div>

              <div className={styles.row}>
                <span className={styles.label}>Auth</span>
                <span className={styles.mono}>
                  {site.auth_type} · {site.auth_user ?? '(sem user)'}
                </span>
                {site.has_secret ? (
                  <span className={styles.mono}>
                    {site.auth_secret_masked}
                  </span>
                ) : (
                  <span className={styles.warn}>sem secret</span>
                )}
              </div>

              <div className={styles.row}>
                <span className={styles.label}>Defaults</span>
                <span className={styles.mono}>
                  status={site.default_status} · author=
                  {site.default_author ?? '—'}
                </span>
              </div>

              {test && (
                <div className={styles.testLine}>
                  <StatusDot state={test.ok ? 'ok' : 'crit'} />
                  <span className={test.ok ? styles.ok : styles.crit}>
                    {test.text}
                  </span>
                </div>
              )}

              <div className={styles.actions}>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => handleTest(site)}
                  loading={testMut.isPending && test?.text === 'A testar…'}
                >
                  Test connection
                </Button>
                <Button size="sm" variant="secondary" onClick={() => openEdit(site)}>
                  Editar
                </Button>
                {site.active && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(site)}
                  >
                    Pausar
                  </Button>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {(creating || editing) && (
        <div className={styles.modalBackdrop} onClick={closeModal}>
          <form
            className={styles.modal}
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleSubmit}
          >
            <h2 className={styles.modalTitle}>
              {editing ? `Editar ${editing.name}` : 'Novo site WordPress'}
            </h2>

            <label className={styles.field}>
              <span>Nome amigável *</span>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Tecnologia GNC"
              />
            </label>

            <label className={styles.field}>
              <span>Slug *</span>
              <input
                required
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder="tecnologiagnc"
                pattern="[a-z0-9_-]+"
                disabled={Boolean(editing)}
              />
            </label>

            <label className={styles.field}>
              <span>URL base *</span>
              <input
                required
                value={form.base_url}
                onChange={(e) =>
                  setForm({ ...form, base_url: e.target.value })
                }
                placeholder="https://exemplo.com"
              />
            </label>

            <label className={styles.field}>
              <span>REST path</span>
              <input
                value={form.rest_path}
                onChange={(e) =>
                  setForm({ ...form, rest_path: e.target.value })
                }
              />
            </label>

            <label className={styles.field}>
              <span>Auth type</span>
              <select
                value={form.auth_type}
                onChange={(e) =>
                  setForm({
                    ...form,
                    auth_type: e.target.value as AuthType,
                  })
                }
              >
                <option value="app_password">Application Password</option>
                <option value="jwt">JWT</option>
                <option value="basic">Basic</option>
              </select>
            </label>

            <label className={styles.field}>
              <span>Utilizador</span>
              <input
                value={form.auth_user}
                onChange={(e) =>
                  setForm({ ...form, auth_user: e.target.value })
                }
                placeholder="email@dominio.com"
              />
            </label>

            <label className={styles.field}>
              <span>
                {editing
                  ? 'Application Password (deixar vazio mantém o actual)'
                  : 'Application Password'}
              </span>
              <input
                type="password"
                value={form.auth_secret}
                onChange={(e) =>
                  setForm({ ...form, auth_secret: e.target.value })
                }
                placeholder={editing ? '••••••••' : 'XXXX XXXX XXXX XXXX'}
                autoComplete="new-password"
              />
            </label>

            <div className={styles.fieldRow}>
              <label className={styles.field}>
                <span>Default author (ID WP)</span>
                <input
                  type="number"
                  min={1}
                  value={form.default_author}
                  onChange={(e) =>
                    setForm({ ...form, default_author: e.target.value })
                  }
                />
              </label>

              <label className={styles.field}>
                <span>Default status</span>
                <select
                  value={form.default_status}
                  onChange={(e) =>
                    setForm({ ...form, default_status: e.target.value })
                  }
                >
                  <option value="publish">publish</option>
                  <option value="draft">draft</option>
                  <option value="pending">pending</option>
                  <option value="private">private</option>
                </select>
              </label>
            </div>

            <label className={styles.field}>
              <span>Notas</span>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
              />
            </label>

            <fieldset className={styles.fieldset}>
              <legend>Mapeamento do publisher (campos custom do WP)</legend>
              <p className={styles.muted}>
                Para cada variável do Wiredesk, informe a <strong>chave de meta</strong> do
                WordPress de destino. Vazio = não envia. As chaves precisam estar registradas
                com <code>show_in_rest</code> no WP (caso contrário o WordPress ignora).
              </p>
              {PUBLISHER_VARS.map((v) => (
                <label key={v.key} className={styles.field}>
                  <span>{v.label}</span>
                  <input
                    value={form.field_map[v.key] ?? ''}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        field_map: { ...form.field_map, [v.key]: e.target.value },
                      })
                    }
                    placeholder={v.placeholder}
                    autoComplete="off"
                  />
                </label>
              ))}
            </fieldset>

            <div className={styles.modalActions}>
              <Button type="button" variant="ghost" onClick={closeModal}>
                Cancelar
              </Button>
              <Button
                type="submit"
                loading={createMut.isPending || updateMut.isPending}
              >
                {editing ? 'Guardar' : 'Criar'}
              </Button>
            </div>

            {msg && <p className={styles.flashIn}>{msg}</p>}
          </form>
        </div>
      )}
    </section>
  );
}
